/**
 * Date/Time utilities for server time emulation and timezone handling.
 * Used for shipping estimates and countdown timers.
 */
(function timeUtils() {
  'use strict';

  // POV object should be available at this point, but it's good to check
  window.POV = window.POV || {};

  // Expose only the needed functions to the main POV object
  POV.Time = {
    isEasternDaylightSavings: isEasternDaylightSavings,
    emulateServerNow: emulateServerNow,
    emulateServerTimeFromBrowser: emulateServerTimeFromBrowser,
    getServerCutoff: getServerCutoff,
    deltaDate: deltaDate,
    isInsideCountdownWindow: isInsideCountdownWindow,
    calculateDaysInTransit: calculateDaysInTransit,
    addDays: addDays,
    formatDateShort: formatDateShort,
    formatDateLong: formatDateLong,
    pluralize: pluralize
  };

  /**
   * Returns whether a date is in EDT (Eastern Daylight Time) or EST (Eastern Standard Time)
   * Uses the browser's built-in timezone database via Intl.DateTimeFormat
   * @param {Date} date A date object
   * @returns {boolean} true if date is during daylight savings (EDT), false if standard time (EST)
   */
  function isEasternDaylightSavings(date) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });

    var parts = formatter.formatToParts(date);
    var timeZoneName = parts.find(function(part) { return part.type === 'timeZoneName'; }).value;

    // EDT = Eastern Daylight Time (DST active)
    // EST = Eastern Standard Time (DST inactive)
    return timeZoneName === 'EDT';
  }

  /**
   * Server time emulated from the browser.
   * Adjusts browser time to Eastern timezone.
   * @returns {Date} Date object representing current Eastern time
   */
  function emulateServerNow() {
    // Browser now
    var now = new Date();

    // Browser and server offsets in minutes
    var browserOffset = now.getTimezoneOffset();
    var serverOffset = isEasternDaylightSavings(now) ? 240 : 300;

    // Return adjusted date obj
    var timeDifference = (browserOffset - serverOffset) * 60 * 1000; // in milliseconds
    return new Date(now.getTime() + timeDifference);
  }

  /**
   * Current server datetime, calculated from the browser time.
   * @returns {Object} Object with hour, day name, and date
   */
  function emulateServerTimeFromBrowser() {
    var serverNow = emulateServerNow();

    var current = {
      hour: serverNow.getHours(),
      day: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][serverNow.getDay()],
      date: serverNow
    };
    return current;
  }

  /**
   * Server cutoff datetime emulated from the browser.
   * @param {number} cutoffHour The hour of the cutoff (0-23)
   * @returns {Object} Object with hour and date of cutoff
   */
  function getServerCutoff(cutoffHour) {
    var serverNow = emulateServerNow();

    // Server time with hours set to cutoff, and minutes/seconds set to 0
    var serverCutoff = new Date(serverNow.setHours(cutoffHour, 0, 0));

    var cutoff = {
      hour: cutoffHour,
      date: serverCutoff
    };
    return cutoff;
  }

  /**
   * Get the components of the time difference between two dates.
   * @param {Date} dateA A date
   * @param {Date} dateB Another date
   * @returns {Object} A delta object containing the difference components.
   */
  function deltaDate(dateA, dateB) {
    // Calculate the time difference
    var time = Math.abs(dateB.getTime() - dateA.getTime());

    // Convert to discrete days/hours/minutes/seconds
    var days = Math.floor(time / (1000 * 60 * 60 * 24));

    var totalHours = Math.floor(time / (1000 * 60 * 60));
    var hours = totalHours % 24;

    var totalMinutes = Math.floor(time / (1000 * 60));
    var minutes = totalMinutes % 60;

    var totalSeconds = Math.floor(time / 1000);
    var seconds = totalSeconds % 60;

    return {
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds,
      time: time,
      totalHours: totalHours,
      totalMinutes: totalMinutes,
      totalSeconds: totalSeconds
    };
  }

  /**
   * Calculates whether countdown should be shown, based on the "display window" setting
   * @param {number} currentHour The current hour
   * @param {number} cutoffHour The cutoff hour
   * @param {number} displayWindow Hours before cutoff to start showing countdown
   * @returns {boolean}
   */
  function isInsideCountdownWindow(currentHour, cutoffHour, displayWindow) {
    var displayStartHour = cutoffHour - displayWindow;
    return currentHour >= displayStartHour && currentHour < cutoffHour;
  }

  /**
   * Calculates the days it takes for an order to arrive after processing
   * @param {Object} current date/time object obtained through emulateServerTimeFromBrowser()
   * @param {Object} settings settings object with cutoffHour, daysBefore, daysAfter
   * @returns {number} number of days
   */
  function calculateDaysInTransit(current, settings) {
    return (current.hour < settings.cutoffHour)
      ? settings.daysBefore
      : settings.daysAfter;
  }

  /**
   * Add N days to a JS date object
   * @param {Date} date The starting date
   * @param {number} n Number of days to add
   * @returns {Date} New date with days added
   */
  function addDays(date, n) {
    return new Date(date.getTime() + (n * 24 * 60 * 60 * 1000));
  }

  /**
   * Format a JavaScript date to a short text format.
   * e.g.: Tue, Nov 5
   * @param {Date} date Passed date.
   * @returns {string} Formatted date.
   */
  function formatDateShort(date) {
    var month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
    var day = date.getDate();
    var dayName = ['Sun,', 'Mon,', 'Tue,', 'Wed,', 'Thu,', 'Fri,', 'Sat,'][date.getDay()];

    return [dayName, month, day].join(' ');
  }

  /**
   * Format a JavaScript date to a long text format.
   * e.g.: Tuesday, November 5
   * @param {Date} date Passed date.
   * @returns {string} Formatted date.
   */
  function formatDateLong(date) {
    var month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][date.getMonth()];
    var day = date.getDate();
    var dayName = ['Sunday,', 'Monday,', 'Tuesday,', 'Wednesday,', 'Thursday,', 'Friday,', 'Saturday,'][date.getDay()];

    return [dayName, month, day].join(' ');
  }

  /**
   * Get the plural or singular form of a word.
   * @param {string} str The singular form of the word
   * @param {number} n The count
   * @returns {string} Singular or plural form
   */
  function pluralize(str, n) {
    return (n === 1) ? str : str + 's';
  }

  // Export for Node.js/Jest (won't affect browser usage)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = POV.Time;
  }
})();
